import { useQuery } from '@tanstack/react-query';
import { Building2, Loader2, AlertTriangle, ShieldAlert } from 'lucide-react';
import { fetchTenants, type TenantRow } from '@/lib/api';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { useRecentRoute } from '@/hooks/useRecentRoute';

/**
 * Super-admin tenant roster (the cross-tenant billing surface).
 *
 * Lists every building operator with its subscription and withholding
 * state, so the platform owner can scan billing health and open any
 * tenant's billing detail. super_admin only: the nav leaf is gated to
 * super_admin and the GET /admin/tenants route 403s everyone else, so a
 * hand-typed URL for a non-super_admin renders an empty error state.
 *
 * A tenant's own admin manages their subscription from Settings instead;
 * they never see this list.
 */

function statusColor(status: string | null): string {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'var(--evi-success, #10b981)';
    case 'past_due':
    case 'unpaid':
      return 'var(--evi-warning, #f59e0b)';
    case 'canceled':
    case 'incomplete':
      return 'var(--evi-error, #ef4444)';
    default:
      return 'var(--color-tx-m)';
  }
}

export function TenantsListPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  useRecentRoute('Operators');

  const tenantsQ = useQuery({
    queryKey: ['tenants'],
    queryFn: fetchTenants,
  });

  const rows = tenantsQ.data ?? [];

  const columns: Column<TenantRow>[] = [
    {
      key: 'name',
      header: 'Operator',
      cell: (t) => (
        <a
          href={`/tenants/${t._id}`}
          onClick={(e) => { e.preventDefault(); onNavigate(`/tenants/${t._id}`); }}
          style={{ color: 'var(--evi-accent, #2ECC71)', fontWeight: 600 }}
        >
          {t.name}
        </a>
      ),
    },
    { key: 'billing_email', header: 'Billing email', cell: (t) => t.billing_email ?? '—' },
    {
      key: 'billing_cadence',
      header: 'Plan',
      cell: (t) => (t.stripe_subscription_id ? (t.billing_cadence === 'annual' ? 'Annual' : 'Monthly') : '—'),
    },
    {
      key: 'subscription_status',
      header: 'Status',
      cell: (t) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: statusColor(t.subscription_status) }}>
            {t.subscription_status ?? 'No subscription'}
          </span>
          {t.withholding_active && (
            <span
              title="Operator session payouts are held while past due."
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '1px 7px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                color: 'var(--evi-warning, #f59e0b)',
                background: 'color-mix(in srgb, var(--evi-warning, #f59e0b) 14%, transparent)',
                border: '1px solid color-mix(in srgb, var(--evi-warning, #f59e0b) 35%, transparent)',
              }}
            >
              <ShieldAlert size={11} /> Withheld
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px 28px' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <Building2 size={20} style={{ color: 'var(--evi-accent, #2ECC71)' }} />
        <h1 style={{ margin: 0, fontSize: 22 }}>Operators</h1>
      </header>

      {tenantsQ.isLoading && (
        <p style={{ color: 'var(--color-tx-m)' }}>
          <Loader2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} className="spin" /> Loading operators…
        </p>
      )}
      {tenantsQ.isError && (
        <p style={{ color: 'var(--evi-error, #ef4444)' }}>
          <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Failed to load operators. This page is super-admin only.
        </p>
      )}
      {!tenantsQ.isLoading && !tenantsQ.isError && rows.length === 0 && (
        <p style={{ color: 'var(--color-tx-m)' }}>No operators found.</p>
      )}
      {rows.length > 0 && (
        <ResponsiveTable<TenantRow>
          rows={rows}
          columns={columns}
          rowKey={(t) => t._id}
        />
      )}
    </div>
  );
}
